/**
 * CLM-113 / CLM-114 — a vendor-list reference: which cloudEQ company's vendor
 * card identifies a Vendor party, and the address that card carried (FR-39,
 * FR-40, DR-12; R-113-7, R-113-9, R-114-5).
 *
 * One `clm.party_source_reference` row per **(cloudEQ entity, vendor number)**
 * — the import's idempotency key. A vendor that supplies two cloudEQ companies
 * is one party with two references. The party itself stays a reference into
 * its system of record (`sor_system 'CLM'` until IR-03, A3), so these schemas
 * describe a bounded **cache** of the Business Central vendor record, never a
 * second master (rule 8): nothing reads the address for a rule.
 *
 * Kept apart from `endpoints/vendor-import.ts` because `domain/party.ts` needs
 * them (`ContractParty.vendor_references`, `PartyAlias.import_batch_id`) and the
 * endpoint module needs `domain/party.ts`'s neighbours — one direction only.
 *
 * `packages/db`'s static suite reads this file by path and asserts that
 * `VENDOR_ADDRESS_SOURCES` equals `ck_party_source_reference_address_source` —
 * keep the `export const X = [...] as const;` form on one line.
 */
import { z } from 'zod';
import { IsoTimestampUtcSchema, bigintIdTextSchema } from '../primitives.js';
import { CloudeqEntityKeySchema } from './cloudeq-entity.js';
/**
 * `clm.vendor_import_batch.import_batch_id` — a `bigint` identity, carried as
 * its decimal text (`bigintIdTextSchema`: `pg` returns `int8` as a string, and
 * a JavaScript number would lose precision above 2^53).
 */
export const VendorImportBatchIdSchema = bigintIdTextSchema('expected a vendor import batch id');
/** The Business Central vendor number, as stored: trimmed and upper-cased, 1..40 (R-113-7). */
export const VENDOR_NO_MAX_LENGTH = 40;
export const VendorNoSchema = z
    .string()
    .min(1)
    .max(VENDOR_NO_MAX_LENGTH)
    .refine((value) => value === value.trim().toUpperCase(), 'a vendor number is stored trimmed and upper-cased');
/** Mirrors `ck_party_source_reference_address_source`. One value until IR-03 widens it (DR-12). */
export const VENDOR_ADDRESS_SOURCES = ['clm_import'];
export const VendorAddressSourceSchema = z.enum(VENDOR_ADDRESS_SOURCES);
/** The seven address columns of a reference, in display order. */
export const VENDOR_ADDRESS_FIELDS = [
    'address_line_1',
    'address_line_2',
    'city',
    'post_code',
    'county',
    'state_code',
    'country_code',
];
export const VendorAddressFieldSchema = z.enum(VENDOR_ADDRESS_FIELDS);
/**
 * The stored bound of each address column, as CLM-113's migration's CHECKs write it. The
 * static suite reads this object by path and compares each number with its
 * CHECK. `country_code` is the ISO alpha-2 code itself.
 */
export const VENDOR_ADDRESS_MAX_LENGTH = Object.freeze({
    address_line_1: 200,
    address_line_2: 200,
    city: 100,
    post_code: 20,
    county: 100,
    state_code: 10,
    country_code: 2,
});
/** ISO 3166-1 alpha-2, as `ck_party_source_reference_country_code` (and `0095`) write it. */
export const IsoCountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, 'expected an ISO 3166-1 alpha-2 code');
const addressText = (max) => z
    .string()
    .min(1)
    .max(max)
    .refine((value) => value.trim().length > 0, 'an address field is never blank — absent is null');
/**
 * A stored address — every column nullable, at least one set (an address with
 * nothing in it is `null`, which is what `ck_party_source_reference_address_pair`
 * holds the source to).
 */
export const VendorAddressSchema = z
    .object({
    address_line_1: addressText(VENDOR_ADDRESS_MAX_LENGTH.address_line_1).nullable(),
    address_line_2: addressText(VENDOR_ADDRESS_MAX_LENGTH.address_line_2).nullable(),
    city: addressText(VENDOR_ADDRESS_MAX_LENGTH.city).nullable(),
    post_code: addressText(VENDOR_ADDRESS_MAX_LENGTH.post_code).nullable(),
    county: addressText(VENDOR_ADDRESS_MAX_LENGTH.county).nullable(),
    state_code: addressText(VENDOR_ADDRESS_MAX_LENGTH.state_code).nullable(),
    country_code: IsoCountryCodeSchema.nullable(),
})
    .refine((address) => VENDOR_ADDRESS_FIELDS.some((field) => address[field] !== null), 'an address with every field empty is null, not an object');
/**
 * One reference as `resolve-party` returns it (`include_references`, R-114-5) —
 * the Party aliases page's "Vendor list" panel. The entity, the vendor number,
 * whether the row is an individual, the address with its source, and the
 * batches. **Never** `individual_reason` or `omitted_aliases`: those are
 * returned only by the Admin import endpoints (R-113-5).
 */
export const VendorReferenceSchema = z
    .object({
    cloudeq_entity_key: CloudeqEntityKeySchema,
    vendor_no: VendorNoSchema,
    is_individual: z.boolean(),
    address: VendorAddressSchema.nullable(),
    address_source: VendorAddressSourceSchema.nullable(),
    first_import_batch_id: VendorImportBatchIdSchema,
    /** The last batch that changed the row (an unchanged re-import touches nothing). */
    last_import_batch_id: VendorImportBatchIdSchema,
    /** When the reference was first written — the panel's "imported *date*". */
    imported_utc: IsoTimestampUtcSchema,
    modified_utc: IsoTimestampUtcSchema,
})
    .superRefine((reference, ctx) => {
    if ((reference.address === null) !== (reference.address_source === null)) {
        ctx.addIssue({ code: 'custom', path: ['address_source'], message: 'an address carries its source, and a source names an address (DR-12)' });
    }
    if (reference.is_individual && reference.address !== null) {
        ctx.addIssue({ code: 'custom', path: ['address'], message: 'an included individual keeps the Name only — never an address (R-113-5)' });
    }
});
/**
 * The reference as the Contract record shows it under a vendor party —
 * "Vendor no. *X* · cloudEQ *entity*" (R-114-5). Empty for a Client or
 * Partner, and for a vendor no list has named.
 */
export const ContractPartyVendorReferenceSchema = z.object({
    cloudeq_entity_key: CloudeqEntityKeySchema,
    vendor_no: VendorNoSchema,
});
//# sourceMappingURL=vendor-reference.js.map